/**
 * TodayTasksBell — the top-bar inbox.
 *
 * Surfaces three things for the signed-in user as a popover hung off the
 * topbar bell, reusing the popover + scroll-list notification pattern the
 * other Ordo apps use:
 *   1. Alerts — the persisted `Notification` feed (PROD-383): payment
 *      failures, cancelled orders, price changes. These
 *      carry real read/unread state; a row click marks it read and follows
 *      its deep link. Rendered FIRST — money problems outrank chores.
 *   2. BlockingTasks that are overdue or due today, grouped Overdue first
 *      then Due today by the same rule as the Tasks page (PROD-1470).
 *   3. Recent onboarding-task comments left by the Ordo team (or co-admins)
 *      on the school's tasks — read-only, no reply obligation (Y-10).
 * The bell's unread dot lights when any section has something. Alerts are
 * deliberately NOT auto-marked-read on popover open: a payment alert
 * deserves an explicit acknowledgment, and auto-marking would kill the dot
 * while unrelated task rows still need it.
 *
 * The popover is headed "Inbox" and each section names its source
 * (PROD-1469): `/notifications` lists only the alerts, so a bell headed
 * "Notifications" over six tasks opened onto an empty page. Task rows take the
 * Tasks page's titles, and "View all tasks" is the primary footer link.
 *
 * Task data is the existing `useMyTasksQuery` feed; the narrowing + grouping
 * is the pure `bellTaskSections` helper so it stays unit-testable. Comment
 * data is the aggregated `useSchoolOnboardingCommentInboxQuery` feed. All
 * three poll every 30s.
 */

import { AppTopBarIconButton } from '@ordo/ui/app-topbar'
import { Badge } from '@ordo/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { ScrollArea } from '@ordo/ui/scroll-area'
import { Bell, MessageSquare } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useMyNotificationsQuery,
} from '@/api/notifications-feed'
import { useSchoolOnboardingCommentInboxQuery } from '@/api/school-onboarding'
import { useMyTasksQuery } from '@/api/tasks'
import { useChefTempLogTasks } from '@/lib/chefs/temp-log'
import { notificationDotClass, notificationTimestamp } from '@/lib/notifications'
import { useProfile } from '@/stores/profile'

import {
  bellTaskSections,
  categoryDotClass,
  displayTitleForTask,
  formatDaysOverdue,
  formatRelativeDate,
  mergeChefTempLogDayRows,
  taskKindLabel,
} from '../tasks/helpers'

interface TodayTasksBellProps {
  /** aria-label for the bell button. */
  label: string
}

/** Trims a comment body to a single-line preview for the notification row. */
function commentPreview(body: string, max = 120): string {
  const collapsed = body.replace(/\s+/g, ' ').trim()
  return collapsed.length > max ? `${collapsed.slice(0, max - 1)}…` : collapsed
}

export function TodayTasksBell({ label }: TodayTasksBellProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // Everyone reads the real BlockingTask inbox; for chefs the raw
  // per-shipment temp-log rows collapse into the synthesized day rows.
  // Mirrors the Tasks page.
  const { profile } = useProfile()
  const isChef = (profile?.profileCodes ?? []).some((c) => c.startsWith('CHEF'))
  const realQuery = useMyTasksQuery()
  const chefTempLog = useChefTempLogTasks(isChef)
  const commentsQuery = useSchoolOnboardingCommentInboxQuery()
  const notificationsQuery = useMyNotificationsQuery()
  const markRead = useMarkNotificationReadMutation()
  const markAllRead = useMarkAllNotificationsReadMutation()

  const taskSections = useMemo(() => {
    const real = realQuery.data?.tasks ?? []
    return bellTaskSections(isChef ? mergeChefTempLogDayRows(real, chefTempLog.tasks) : real)
  }, [isChef, chefTempLog.tasks, realQuery.data])
  const comments = commentsQuery.data?.comments ?? []
  // Unread alerts only — a read alert lives on the /notifications page, not
  // in the bell (the bell is the "needs my attention" surface).
  const alerts = useMemo(
    () => (notificationsQuery.data?.notifications ?? []).filter((n) => !n.readAt),
    [notificationsQuery.data],
  )

  const taskCount = taskSections.reduce((n, section) => n + section.tasks.length, 0)
  const commentCount = comments.length
  const alertCount = alerts.length
  const total = taskCount + commentCount + alertCount

  function goToTasks() {
    setOpen(false)
    // The Tasks inbox renders the per-subKind completion sheet (chefs
    // reach the temp-log sheet from there).
    navigate('/tasks')
  }

  function goToTaskComments(taskId: string) {
    setOpen(false)
    navigate(`/onboarding/${encodeURIComponent(taskId)}`)
  }

  function openAlert(alert: { id: string; deepLinkUri: string | null }) {
    markRead.mutate(alert.id)
    if (alert.deepLinkUri) {
      setOpen(false)
      navigate(alert.deepLinkUri)
    }
  }

  function goToAllNotifications() {
    setOpen(false)
    navigate('/notifications')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <AppTopBarIconButton
          icon={<Bell className="h-5 w-5" />}
          unread={total > 0}
          aria-label={label}
          // Ordo is customer-facing: keep its existing 32px bell rather than
          // the internal apps' 36px top-bar button.
          className="h-8 w-8 text-foreground/70"
        />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold">Inbox</p>
          {total > 0 ? (
            <Badge variant="secondary" className="text-[10px]">
              {total}
            </Badge>
          ) : null}
        </div>

        {total === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center text-muted-foreground">
            <Bell className="h-7 w-7 opacity-40" />
            <p className="text-sm">Nothing new</p>
            <p className="text-xs">You're all caught up.</p>
          </div>
        ) : (
          <ScrollArea className="max-h-96">
            {alertCount > 0 ? (
              <section>
                <div className="flex items-center justify-between px-4 pb-1 pt-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Notifications
                  </p>
                  <button
                    type="button"
                    onClick={() => markAllRead.mutate()}
                    className="text-[11px] font-medium text-primary hover:underline">
                    Mark all read
                  </button>
                </div>
                <ul className="divide-y">
                  {alerts.map((alert) => (
                    <li key={alert.id}>
                      <button
                        type="button"
                        onClick={() => openAlert(alert)}
                        className="flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-muted/50">
                        <span
                          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notificationDotClass(alert.category)}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium leading-tight">{alert.title}</span>
                          {alert.body ? (
                            <span className="mt-0.5 block text-[13px] text-muted-foreground">{alert.body}</span>
                          ) : null}
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            {notificationTimestamp(alert.createdAt)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {taskCount > 0 ? (
              <section className={alertCount > 0 ? 'border-t' : undefined}>
                <p className="px-4 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Tasks
                </p>
                {taskSections.map((section, index) => (
                  <section key={section.title} className={index > 0 || alertCount > 0 ? 'border-t' : undefined}>
                    <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {section.title}
                    </p>
                    <ul className="divide-y">
                      {section.tasks.map((task) => {
                        const pastDue = task.dueAt ? new Date(task.dueAt).getTime() < Date.now() : false
                        // The label follows the group, not formatRelativeDate, which reads a
                        // 5pm Pacific daylight due time (exactly UTC midnight) as "tomorrow".
                        const dueLabel = task.dueAt && section.overdue ? formatDaysOverdue(task.dueAt) : 'Due today'
                        return (
                          <li key={task.id}>
                            <button
                              type="button"
                              onClick={goToTasks}
                              className="flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-muted/50">
                              <span
                                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${categoryDotClass(task.category)}`}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium leading-tight">
                                  {displayTitleForTask(task)}
                                </span>
                                {task.subKind ? (
                                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                                    {taskKindLabel(task.subKind)}
                                  </span>
                                ) : null}
                                {task.dueAt ? (
                                  <span
                                    className={`mt-0.5 block text-[11px] ${pastDue ? 'text-destructive' : 'text-muted-foreground'}`}>
                                    {dueLabel}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                ))}
              </section>
            ) : null}

            {commentCount > 0 ? (
              <section className={taskCount > 0 || alertCount > 0 ? 'border-t' : undefined}>
                <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Comments
                </p>
                <ul className="divide-y">
                  {comments.map((comment) => (
                    <li key={comment.id}>
                      <button
                        type="button"
                        onClick={() => goToTaskComments(comment.taskId)}
                        className="flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-muted/50">
                        <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm leading-tight">
                            <span className="font-medium">{comment.author?.name ?? 'Ordo team'}</span>
                            <span className="text-muted-foreground"> on {comment.taskTitle}</span>
                          </span>
                          <span className="mt-0.5 block text-[13px] text-muted-foreground">
                            {commentPreview(comment.body)}
                          </span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            {formatRelativeDate(comment.createdAt)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </ScrollArea>
        )}

        <div className="flex items-center justify-between border-t px-4 py-2.5">
          <button type="button" onClick={goToTasks} className="text-xs font-semibold text-primary hover:underline">
            View all tasks
          </button>
          {/* Shown even with no unread alerts: the bell is the only way to /notifications. */}
          <button
            type="button"
            onClick={goToAllNotifications}
            className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline">
            All notifications
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
